import { readPageScale } from '@/lib/page-scale-storage';
import { readViewSetting } from '@/lib/view-mode-storage';
import firebase from 'firebase/app';
import 'firebase/functions';
import { db } from '@/main';
import profileByUID from '@/lib/user-profiles';
import Utils from '@/lib/Utils';
import resolveCueContext from '@/lib/resolve-cue-context';
import { hasAnyLiveFlag } from '@/lib/featureFlags';
import isPrintPath from '@/lib/print-route';
import { splitLayerIDs, followsScript } from '@/lib/split-layers';

// When true, a pinned project shows only in the Pinned section (not duplicated
// in Recent). Single toggle point for that product decision.
const PINNED_EXCLUDED_FROM_RECENT = true;

// Owner-username cache so list view doesn't fire one uncached Firestore read
// per row. Keyed by uid; module scope so it persists across getter re-runs.
const ownerNameCache = new Map();

export default {
    projects: (state) => { return state.projects || {}; },
    mostRecentProject: (state, getters) => {
        const userProjects = getters.userProjects;
        return userProjects.length > 0 ? userProjects[0] : null;
    },
    userProjects: (state, getters) => {
        const user = getters.user;
        if (!user) return [];
        const userProjectIds = Object.keys(user.projects || {});
        const projects = getters.projects;
        
        return Object.values(projects)
            .filter(project => userProjectIds.includes(project.id))
            .filter(project => Object.keys(project.collaborators || {}).includes(user.uid) || project.ownerUID === user.uid)
            .sort((a, b) => {
                const timeA = new Date(a.lastChangedDate);
                const timeB = new Date(b.lastChangedDate);
                return timeB - timeA;
            });
    },
    // Pinned projects appear ONLY in the Pinned section; a pinned project is
    // never also listed under Recent (flip PINNED_EXCLUDED_FROM_RECENT to false
    // to let Recent include everything). The pinned flag is a per-user, per-project
    // boolean at users/{uid}.projects[id].pinned (sibling of dashboardHide).
    pinnedProjects: (state, getters) => {
        const up = (getters.user && getters.user.projects) || {};
        return getters.userProjects.filter(p => up[p.id] && up[p.id].pinned);
    },
    recentProjects: (state, getters) => {
        const up = (getters.user && getters.user.projects) || {};
        return PINNED_EXCLUDED_FROM_RECENT
            ? getters.userProjects.filter(p => !(up[p.id] && up[p.id].pinned))
            : getters.userProjects;
    },
    invitations: (state) => { return state.invitations || []; },
    userInvitations: (state, getters) => {
        const user = getters.user;
        if (!user) return [];
        const userEmail = (user.email || '').toLowerCase();
        return getters.invitations.filter(
            invitation => (invitation.email || '').toLowerCase() === userEmail
        );
    },
    affiliateInvitations: (state) => { return state.affiliateInvitations || []; },
    // Invites addressed to the current user (dashboard notifications). Lowercase
    // BOTH sides — invites are stored lowercased, but auth emails may be mixed
    // case, and the sibling userInvitations getter's un-lowercased compare is a
    // known casing bug we do not want to copy.
    userAffiliateInvitations: (state, getters) => {
        const user = getters.user;
        if (!user || !user.email) return [];
        const email = user.email.toLowerCase();
        // A failed invite (the invitee has their own subscription) is shown to
        // the ADMIN with the reason; the invitee has already been told.
        return getters.affiliateInvitations.filter(
            invitation => (invitation.email || '').toLowerCase() === email && invitation.status !== 'failed'
        );
    },
    // Is there already a pending affiliate invite for this email from this admin?
    // Guards against duplicate invites (mirrors userByEmailPendingInvitation).
    affiliateInvitationPending: () => async (email, adminUid) => {
        try {
            const snap = await db.collection('affiliateInvitations')
                .where('email', '==', (email || '').toLowerCase())
                .where('adminUid', '==', adminUid)
                .get();
            // A failed invite does not block inviting the same person again
            // once they have cancelled their own subscription.
            const live = snap.docs.map(doc => doc.data()).filter(inv => inv.status !== 'failed');
            return live.length > 0 ? live[0] : null;
        } catch (error) {
            console.error('Error checking pending affiliate invitation:', error);
            return null;
        }
    },
    userPendingProjects: async(state, getters) => {
        const pendingProjectIds = getters.userInvitations.map(invitation => invitation.pid);
        if (pendingProjectIds.length > 0) {
            const projectQuery = await db.collection('projects').where('id', 'in', pendingProjectIds).get();
            const projects = projectQuery.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return projects;
        }
        return [];
    },
    getProject: () => async (id) => {   
        try {
            const projectDoc = await db.collection('projects').doc(id).get();
            if (projectDoc.exists) {
                const projectData = projectDoc.data() || {};
                // Always prefer the document id — data().id is not guaranteed.
                return { ...projectData, id: projectDoc.id };
            }
            return null;
        } catch (error) {
            console.error('Error fetching project:', error);
            return null;
        }
    },
    projectFromID: (state, getters) => async(id) => {
        const project = await getters.getProject(id);
        return project || null;
    },
    projectsPendingCollaborators: state => (id) => { return Object.values(state.projectsPendingCollaborators[id]); },

    // User Getters
    users: (state) => { return state.users; },
    user: (state) => { return state.activeUser; },
    // Early-adopter feature flags (featureFlags/{uid}). hasFlag() defaults to
    // false for missing docs/keys, so the stable code path is always the default.
    featureFlags: (state) => { return state.featureFlags; },
    // userPaused is the self-service escape hatch: a member can turn their own
    // preview OFF (but not back on — an admin resumes). It suppresses membership
    // and every flag at read time, without touching the admin's grant.
    isEarlyAdopter: (state) => {
        const ff = state.featureFlags;
        return !!(ff && ff.isEarlyAdopter && !ff.userPaused);
    },
    // Is there a LIVE pre-release feature this member can actually preview?
    // Gates the badge only -- never the bug reporter, which is deliberately tied
    // to cohort membership so it survives the flag list emptying (step 55).
    hasAnyLiveFlag: state => hasAnyLiveFlag(state.featureFlags),
    hasFlag: state => (key) => {
        const ff = state.featureFlags;
        return !!(ff && !ff.userPaused && ff.enabled && ff.enabled[key] === true);
    },
    // Rules plan S5a (23 Sept 2026): another user's PROFILE via the getUserProfiles
    // callable, batched and cached (lib/user-profiles.js), not their whole document.
    // Same signature and the same null-on-failure as before.
    userByUID: () => uid => profileByUID(uid),
    // Resolve an owner's display label for the dashboard: "me" for the current
    // user (no fetch), otherwise the cached username. Short-circuits self before
    // awaiting and caches other users so N list rows don't do N reads.
    ownerLabelByUID: (state, getters) => async (uid) => {
        if (!uid) return '';
        if (getters.user && getters.user.uid === uid) return 'me';
        if (ownerNameCache.has(uid)) return ownerNameCache.get(uid);
        const owner = await getters.userByUID(uid);
        const name = (owner && owner.username) || '';
        ownerNameCache.set(uid, name);
        return name;
    },
    // Rules plan S3 (23 Sept 2026): answered by the lookupUserByEmail callable, not
    // a client query. It returns {uid, username} for any match, plus the booleans
    // affiliatedWithCaller / selfPays when the caller grants seats. NOT the whole
    // record any more: the old query handed the browser a stranger's stripe_id to
    // answer a yes/no question, and it is what blocked scoping /users reads.
    // On any failure (including the rate limit) it returns null, as the old
    // query did, so an invite still goes ahead.
    userByEmail: () => async (userByEmail) => {
        try {
            const res = await firebase.functions().httpsCallable('lookupUserByEmail')({ email: userByEmail });
            const found = res && res.data;
            return found && found.exists ? found : null;
        } catch (error) {
            console.error('Error looking up user by email:', error && error.message);
            return null;
        }
    },
    userByEmailPendingInvitation: () => async (email, pid) => {   
        try {
            const userDoc = await db.collection('invitations').where('email', '==', email).where('pid', '==', pid).get();
            if (userDoc.docs.length > 0) {
                const userData = userDoc.docs[0].data();
                return userData || null;
            }
            return null;
        } catch (error) {
            console.error('Error fetching user:', error);
            return null;
        }
    },
    userProjectData: (state, getters) => {
        try {
            const currentUser = getters.user;
            const currentProject = getters.project;
            if (currentUser && currentUser.projects && currentProject && currentProject.id) {
                return currentUser.projects[currentProject.id] || {};
            }
        } catch (e) {
            console.error(e);
        }
        return {};
    },
    userProjectByUidData: (state, getters) => {
        try {
            const currentProject = getters.project;
            const currentUser = getters.userByUID(currentProject && currentProject.ownerUID);
            if (currentUser && currentUser.projects && currentProject && currentProject.id) {
                return currentUser.projects[currentProject.id] || {};
            }
        } catch (e) {
            console.error(e);
        }
        return {};
    },
    // Project Getters
    project: (state) => {
        if (state.activeProject && state.activeProject.isSample) {
            return {
                ...state.activeProject,
                ...state.activeProjectData,
                locked: false,
                pageSize: {
                    ...state.activePageSize
                }
            };
        }
        return {
            ...state.activeProject,
            ...state.activeProjectData,
            pageSize: {
                ...state.activePageSize
            }
        };
    },
    notesListCounts: state => state.notesListCounts,
    projectTitle: (state, getters) => {
        return getters.project.title || '';
    },
    projectID: (state, getters) => {
        return getters.project.id || '';
    },
    projectPath: (state, getters) => {
        if (getters.currentVersion) {
            return `/projects/${getters.project.id}/versions/${getters.currentVersion.id}`;
        }

        return `/projects/${getters.project.id}`;
    },
    documentUrl: (state, getters) => {
        if (getters.currentVersion && getters.currentVersion.documentUrl) {
            return getters.currentVersion.documentUrl;
        }
        return (state.activeProject && state.activeProject.documentUrl) || '';
    },
    ownerUID: (state) => {
        return (state.activeProject && state.activeProject.ownerUID) || null;
    },

    // Version Getters
    versions: (state, getters) => {
        const versions = getters.project.versions || [];
        // Handle both array (from vuexfire) and object formats
        return Array.isArray(versions) ? versions : Object.values(versions);
    },
    versionByID: (state, getters) => (id) => {
        const vFilter = getters.versions.filter(v => v.id === id);
        if (vFilter.length > 0) {
            return vFilter[0];
        }
        return null;
    },
    currentVersion: (state) => {
        if (state.activeVersion) {
            return {
                ...state.activeVersion,
                ...state.activeVersionData
            };
        }
        return null;
    },
    isLocked: (state, getters) => {
        if (getters.currentVersion) {
            return getters.currentVersion.locked || false;
        }   
        return getters.project.locked || false;
    },

    /**
     * Who is ALLOWED to edit Structure (Act/Scene/Song), ignoring Project Lock.
     * Owner and administrators always; members only if canEditStructure is granted.
     *
     * Lock is deliberately NOT part of this. Lock is a temporary state of the
     * project, not a change to who has the permission, so the UI uses this to
     * decide whether the Structure tool is YOURS TO SEE and canEditStructure
     * below to decide whether it is usable RIGHT NOW. Toggling lock used to make
     * the toolbar icon vanish, which read as "my permission was revoked" and
     * generated support mail. Do not fold isLocked back in here.
     */
    hasStructurePermission: (state, getters) => {
        const user = getters.user;
        if (!user || !user.uid) {
            return false;
        }
        if (getters.ownerUID === user.uid) {
            return true;
        }
        const collab = getters.collaboratorByUID(user.uid);
        if (!collab) {
            return false;
        }
        if (collab.type === 'administrator') {
            return true;
        }
        return collab.canEditStructure === true;
    },

    /**
     * Tool-level Structure (Act/Scene/Song) edit permission, right now.
     * Permission AND not locked. Every write path gates on this; nobody edits
     * Structure while the project is locked, owner included.
     */
    canEditStructure: (state, getters) => {
        if (getters.isLocked) {
            return false;
        }
        return getters.hasStructurePermission;
    },
    // project props
    projectProps: (state) => {
        return state.activeProjectProps || {};
    },
    projectPages: (state, getters) => {
        return getters.projectProps.pages || [];
    },
    projectTotalPages: (state, getters) => {
        return getters.projectPages.length;
    },

    // Cue Getters
    cues: (state, getters) => {
        let cues = getters.project.cues || [];
        const version = getters.currentVersion;
        if (version) {
            cues = version.cues || [];
        }
        return Object.values(cues);
    },
    cuesLength: (state, getters) => {
        return getters.cues.length;
    },
    cuesByPage: (state, getters) => (pageNumber) => {
        return getters.cues.filter(cue => cue.pageNumber === pageNumber);
    },
    allVisibleCues: (state, getters) => {
        const layerIDs = getters.visibleLayerIDs;
        return getters.cues.filter((cue) => {
            return layerIDs.includes(cue.layer);
        });
    },
    allVisibleListCues: (state, getters) => {
        const layerIDs = getters.visibleListLayerIDs;
        return getters.cues.filter((cue) => {
            return layerIDs.includes(cue.layer);
        });
    },
    visibleCues: (state, getters) => (pageNumber) => {
        const layerIDs = getters.visibleLayerIDs;
        return getters.cuesByPage(pageNumber).filter((cue) => {
            return layerIDs.includes(cue.layer) && (getters.showCuesLayerIDs !== null && getters.showCuesLayerIDs.includes(cue.layer));
        });
    },
    worknoteCues: (state, getters) => {
        const layerIDs = getters.visibleLayerIDs;
        return getters.cues.filter(cue => cue.data && cue.data.workNote).filter(cue => layerIDs.includes(cue.layer));
    },
    cuesPerLayer: (state, getters) => (layerID) => {
        return getters.cues.filter(cue => cue.layer === layerID);
    },

    // Structure (Act / Scene / Song) markers for whatever is currently OPEN — the
    // saved version when one is being viewed, the live project otherwise.
    //
    // Version-aware deliberately, and it MUST stay in step with `projectPath`,
    // which already resolves to the version's document when one is open. Editing a
    // version is a supported workflow, not an accident: one user can prepare fixes
    // in a version while another calls the show from the live project, then promote
    // it later. Every marker WRITE already followed `projectPath` into the version;
    // until 23 Aug the READS did not, so the manage-structure dialog reported "no
    // structure" for a version whose structure was plainly visible on the page.
    //
    // This supersedes the 20 Aug interim suppression and the short-lived display/
    // live getter split. That split existed because EMPTYING this getter would have
    // made the page-op shift actions iterate nothing and silently skip the shift.
    // Pointing it at the version instead does not have that problem: in the live
    // view it returns exactly what it always did, and in version view the shift
    // actions write to the version anyway, via `projectPath`.
    //
    // A version saved before #386 has no scriptMarkers subcollection, so this
    // yields [] and it renders with no structure — deliberate. That data was never
    // recorded and cannot be recovered. No schema flag is needed to tell that from
    // "genuinely had no markers": both should show nothing, and both do.
    //
    // For the LIVE set specifically, regardless of what is open, read
    // `project.scriptMarkers` — as versions-nav does when warning about promotion.
    scriptMarkers: (state, getters) => {
        if (getters.currentVersion) {
            return Object.values(state.activeVersionData.scriptMarkers || {});
        }
        return Object.values(getters.project.scriptMarkers || []);
    },
    scriptMarkersByPage: (state, getters) => (pageNumber) => {
        return getters.scriptMarkers.filter(m => m.pageIndex === pageNumber);
    },
    // What actually RENDERS on a script page, honouring the viewer's own
    // "hide structure marks on script" preference.
    //
    // Deliberately a separate getter rather than a filter inside
    // scriptMarkersByPage: marker-dialog.vue computes a new mark's vertical
    // cascade offset from that getter, and must keep counting HIDDEN marks.
    // Filter it there and a user with structure hidden stacks every new mark
    // at the same y.
    //
    // Only page.vue (script pages, and split view via pages.vue) and
    // print-page.vue read this. The nav panel and cue list use
    // scriptMarkersNav and are deliberately unaffected — hiding is scoped to
    // the script itself, so the marks stay listed where you manage them.
    visibleScriptMarkersByPage: (state, getters) => (pageNumber) => {
        return getters.hideStructureMarks ? [] : getters.scriptMarkersByPage(pageNumber);
    },
    scriptMarkersNav: (state, getters) => {
        return [...(getters.scriptMarkers || [])].sort((a, b) => {
            if (a.pageIndex !== b.pageIndex) {
                return a.pageIndex - b.pageIndex;
            }
            if ((a.y || 0) !== (b.y || 0)) {
                return (a.y || 0) - (b.y || 0);
            }
            return (a.x || 0) - (b.x || 0);
        });
    },
    cueWithContext: (state, getters) => (cueId) => {
        const cue = getters.cues.find(c => c.id === cueId);
        if (!cue) {
            return null;
        }
        return {
            ...cue,
            context: resolveCueContext(cue, getters.scriptMarkers)
        };
    },

    // Layers Getters
    layers: (state, getters) => {
        let layers = getters.project.layers || [];
        const version = getters.currentVersion;
        
        if (version) {
            layers = version.layers || [];
        }
        return Object.values(layers).sort((a, b) => {
            const aTitle = a.title.toUpperCase();
            const bTitle = b.title.toUpperCase();
            if (aTitle > bTitle) {
                return 1;
            }
            if (aTitle < bTitle) {
                return -1;
            }
            return 0;
        });
    },
    layerByID: (state, getters) => (id) => {
        try {
            const filter = getters.layers.filter(layer => layer.id === id);
            if (filter.length > 0) return filter[0];
        } catch (e) {
            console.error(e);
        }

        return null;
    },
    layerByCue: (state, getters) => (cue) => {
        return getters.layerByID(cue.layer);
    },
    getDefaultCuePlacement: (state, getters) => (layer) => {
        const layers = getters.userProjectData.layers;
        if (layers && layers[layer] && layers[layer].defaultCuePlacement) {
            if (getters.selectedPageType === 'no' && getters.showMarginView) {
                return getters.showMarginView ? `${layers[layer].defaultCuePlacement}Edge` : layers[layer].defaultCuePlacement;
            }
            return layers[layer].defaultCuePlacement;
        }
        if (getters.selectedPageType === 'no' && getters.showMarginView) {
            return getters.cuePlacement === 'right' ? 'rightEdge' : 'leftEdge';
        }
        return getters.cuePlacement === 'right' ? 'right' : 'left';
    },
    getDefaultCuePlacementPrint: (state, getters) => (layer) => {
        const layers = getters.userProjectData.layers;
        if (layers && layers[layer] && layers[layer].defaultCuePlacement) {
            return layers[layer].defaultCuePlacement;
        }
        return getters.cuePlacement === 'right' ? 'right' : 'left';
    },
    printCuesInMargin: () => {
        // Read at print time from the popup window (set by the toolbar print menu).
        return localStorage.getItem('print-cues-in-margin') === 'true';
    },
    // Print-only placement. Separate from getDefaultCuePlacementPrint (which the
    // EDITOR's layer list also uses — overloading it would leak the margin flag
    // into the editor). When "Include cues in margin" is on, append "Edge" so the
    // cue cards translate off the page into the margin (mirrors editor Margin View;
    // see print/cues/cue.vue leftEdge/rightEdge branches).
    getPrintCuePlacement: (state, getters) => (layer) => {
        const base = getters.getDefaultCuePlacementPrint(layer);
        const inMargin = getters.printCuesInMargin && getters.selectedPageType === 'no';
        return inMargin ? `${base}Edge` : base;
    },
    getLayerBoxOpacity: (state, getters) => (cue) => {
        if (getters.cueBoxOpacityOptions[cue.id] >= 0) {
            return getters.cueBoxOpacityOptions[cue.id];
        }
        if (cue.boxOpacity) {
            return cue.boxOpacity;
        }

        const layers = getters.userProjectData.layers;
        if (layers && layers[cue.layer] && layers[cue.layer].boxOpacity >= 0) {
            return layers[cue.layer].boxOpacity;
        }
        return 20;
    },
    getCueLineOpacity: (state, getters) => (cue) => {
        if (getters.cueLineOpacityOptions[cue.id] >= 0) {
            return getters.cueLineOpacityOptions[cue.id];
        }
        if (cue.lineOpacity) {
            return cue.lineOpacity;
        }
        const layers = getters.userProjectData.layers;
        if (layers && layers[cue.layer] && layers[cue.layer].lineOpacity >= 0) {
            return layers[cue.layer].lineOpacity;
        }
        return 30;
    },
    getLayerAutoNumbering: (state, getters) => (layerID) => {
        const layers = getters.userProjectData.layers;
        if (layers && layers[layerID]) {
            return !!layers[layerID].autoNumbering;
        }
        return false;
    },
    getLayerAutoNumberStart: (state, getters) => (layerID) => {
        const layers = getters.userProjectData.layers;
        if (layers && layers[layerID] && Utils.isValidCueNumberSeed(layers[layerID].autoNumberStart)) {
            return Number(layers[layerID].autoNumberStart);
        }
        return null;
    },
    // The seed to offer when Auto Numbering is switched ON with no starting
    // number: one interval past the highest NUMERIC cue title already on that
    // layer, or 1 if it has none.
    //
    // Josh, 14 Sept 2026, choosing this over a flat 1: someone turning auto
    // numbering on mid-show wants it to continue their sequence, not restart it
    // and collide with every cue they already have.
    //
    // ⚠ Cue titles are FREE TEXT -- "Q5", "A1", "" and "  " are all legal -- so
    // anything that is not a real number is ignored rather than coerced. Note
    // Number('  ') is 0, which is why the trim check is here and why
    // Utils.isValidCueNumberSeed (written for the seed FIELD) is not enough.
    nextCueNumberForLayer: (state, getters) => (layerID) => {
        const numbers = Object.values(getters.project.cues || {})
            .filter(cue => cue && cue.layer === layerID)
            .map((cue) => {
                const raw = cue.title;
                if (typeof raw !== 'string' || raw.trim() === '') return NaN;
                return Number(raw);
            })
            .filter(n => Number.isFinite(n));
        if (numbers.length === 0) return 1;
        // reduce, not Math.max(...numbers) -- spreading a few thousand cues at
        // once can overflow the call stack.
        const highest = numbers.reduce((max, n) => {
            return n > max ? n : max;
        }, numbers[0]);
        return Utils.roundCueNumber(highest + getters.getLayerAutoNumberInterval(layerID));
    },
    getLayerAutoNumberInterval: (state, getters) => (layerID) => {
        const layers = getters.userProjectData.layers;
        if (layers && layers[layerID] && Utils.isValidCueInterval(layers[layerID].autoNumberInterval)) {
            return Number(layers[layerID].autoNumberInterval);
        }
        return 1;
    },
    getCueFontSize: (state, getters) => (cue) => {
        if (getters.titleFontSizeOptions[cue.id]) {
            return getters.titleFontSizeOptions[cue.id];
        }
        const layers = getters.userProjectData.layers;
        if (layers && layers[cue.layer] && layers[cue.layer].titleFontSize) {
            return layers[cue.layer].titleFontSize;
        }
        return 14;
    },
    visibleLayers: (state, getters) => {
        return getters.visibleLayerIDs.reduce((result, id) => {
            const layer = getters.layerByID(id);
            if (layer) result.push(layer);

            return result;
        }, []);
    },
    visibleLayerIDs: (state, getters) => {
        try {
            const userProjectData = getters.userProjectData;
            if (userProjectData && userProjectData.layers) {
                return userProjectData.layers.viewing || [];
            }
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    visibleListLayers: (state, getters) => {
        return getters.visibleListLayerIDs.reduce((result, id) => {
            const layer = getters.layerByID(id);
            if (layer) result.push(layer);
            return result;
        }, []);
    },
    visibleListLayerIDs: (state, getters) => {
        try {
            const userProjectData = getters.userProjectData;
            if (userProjectData && userProjectData.layers) {      
                return userProjectData.layers.listViewing || [];
            }
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    // Split view's own layer choice (layers.splitViewing; absent = follow the script's
    // layers.viewing). Deleted layers are dropped. See src/lib/split-layers.js.
    visibleSplitLayerIDs: (state, getters) => {
        const data = getters.userProjectData;
        return splitLayerIDs(data && data.layers, getters.layers.map(layer => layer.id));
    },
    splitLayersFollowScript: (state, getters) => {
        const data = getters.userProjectData;
        return followsScript(data && data.layers);
    },
    visibleSplitLayers: (state, getters) => {
        return getters.visibleSplitLayerIDs.map(id => getters.layerByID(id)).filter(Boolean);
    },
    allVisibleSplitCues: (state, getters) => {
        const layerIDs = getters.visibleSplitLayerIDs;
        return getters.cues.filter(cue => layerIDs.includes(cue.layer));
    },
    showNotesLayers: (state, getters) => {
        return getters.showNotesLayerIDs.reduce((result, id) => {
            const layer = getters.layerByID(id);
            if (layer) result.push(layer);

            return result;
        }, []);
    },
    showNotesLayerIDs: (state, getters) => {
        try {
            const userProjectData = getters.userProjectData;
            if (userProjectData && userProjectData.layers) {
                return userProjectData.layers.showNotes || null;
            }
        } catch (e) {
            console.error(e);
        }
        return null;
    },
    showCuesLayers: (state, getters) => {
        return (getters.showCuesLayerIDs && getters.showCuesLayerIDs.reduce((result, id) => {
            const layer = getters.layerByID(id);
            if (layer) result.push(layer);

            return result;
        }, [])) || [];
    },
    showCuesLayerIDs: (state, getters) => {
        try {
            const userProjectData = getters.userProjectData;
            if (userProjectData && userProjectData.layers) {
                return userProjectData.layers.showCues || null;
            }
        } catch (e) {
            console.error(e);
        }
        return null;
    },
    // --- Production Pass entitlement -------------------------------------
    // Phase 1 is parity-preserving: `project.unlocked` and `user.showPasses`
    // are absent today, so these getters equal the current `active_pay`/`issrc`
    // behavior. A Production Pass ($299 one-time) sets `project.unlocked` on one
    // project and increments the buyer's `showPasses` credit.

    // Is the ACTIVE project's editing paid for? Generalizes the `active_pay || issrc`
    // primitive used across the editor with a per-project `unlocked` flag. Does NOT
    // include isSample or owner/collaborator — callers keep their own such wrapping.
    activeProjectEntitled: (state, getters) => {
        const user = getters.user;
        if (!user) return false;
        const p = state.activeProject || {};
        return !!(user.active_pay || p.issrc || p.unlocked);
    },
    // Full "can the current user edit the active project" — drives the read-only banner.
    canEditActiveProject: (state, getters) => {
        const user = getters.user;
        if (!user) return false;
        const p = state.activeProject || {};
        const entitled = user.active_pay || p.issrc || p.unlocked || p.isSample;
        if (!entitled) return false;
        return getters.ownerUID === user.uid || !!getters.collaboratorByUID(user.uid);
    },
    // Can the current user create/own a new project? A subscriber (active_pay) OR a
    // holder of an unused Production Pass. Counter-based on the always-loaded user
    // doc, so it can't fail open in the editor like a `userProjects` count would.
    canCreateProject: (state, getters) => {
        const user = getters.user;
        if (!user) return false;
        return !!(user.active_pay || (user.showPasses || 0) > 0);
    },
    // Is this a paying customer at all (subscriber or pass holder)? Drives account
    // status/billing display so a pass buyer isn't shown as a free "Observer".
    isPaidCustomer: (state, getters) => {
        const user = getters.user;
        if (!user) return false;
        return !!(user.active_pay || (user.showPasses || 0) > 0);
    },
    editableLayers: (state, getters) => {
        const user = getters.user;
        if (!user) return [];
        if (user) {
            if (state.activeProject && state.activeProject.isSample) {
                if (getters.ownerUID === user.uid) return getters.layers;

                const collab = getters.collaboratorByUID(user.uid);
                if (collab) {
                    return getters.layers.filter(layer => collab.layers[layer.id]);
                }
            }
            if (!getters.activeProjectEntitled) return [];
            if (getters.ownerUID === user.uid) return getters.layers;

            const collab = getters.collaboratorByUID(user.uid);
            if (collab) {
                return getters.layers.filter(layer => collab.layers[layer.id]);
            }
        }
        return [];
    },
    viewLayers: (state, getters) => {
        const user = getters.user;
        if (getters.ownerUID !== user.uid) return getters.layers;

        const collab = getters.collaboratorByUID(user.uid);
        if (collab) {
            return getters.layers.filter(layer => collab.layers[layer.id]);
        }
        return [];
    },
    validLayers: (state, getters) => {
        if (getters.editableLayers.length > 0 && getters.visibleLayers.length > 0) {
            const editSet = new Set(getters.editableLayers.map(x => x.id));
            const visibleSet = new Set(getters.visibleLayers);
            const choices = Array.from(visibleSet).filter(
                x => editSet.has(x.id)
            );
            return choices;
        }
        return [];
    },
    validNoteLayers: (state, getters) => {
        if (getters.editableLayers.length > 0 && getters.showNotesLayers.length > 0) {
            const editSet = new Set(getters.editableLayers.map(x => x.id));
            const visibleSet = new Set(getters.showNotesLayers);
            const choices = Array.from(visibleSet).filter(
                x => editSet.has(x.id)
            );
            return choices;
        }
        return [];
    },
    validCueLayers: (state, getters) => {
        if (getters.editableLayers.length > 0 && getters.showCuesLayers.length > 0) {
            const editSet = new Set(getters.editableLayers.map(x => x.id));
            const visibleSet = new Set(getters.showCuesLayers);
            const choices = Array.from(visibleSet).filter(
                x => editSet.has(x.id)
            );
            return choices;
        }
        return [];
    },
    currentLayer: (state, getters) => {
        const userProjectData = getters.userProjectData;
        if (userProjectData && userProjectData.layers && userProjectData.layers.editing) {
            const editLayerID = userProjectData.layers.editing;
            if (getters.validLayers.some(l => l.id === editLayerID)) {
                return getters.layerByID(editLayerID);
            }
        }
        return null;
    },
    currentNoteLayer: (state, getters) => {
        const userProjectData = getters.userProjectData;
        if (userProjectData && userProjectData.layers && userProjectData.layers.noting) {
            const editLayerID = userProjectData.layers.noting;
            if (getters.validNoteLayers.some(l => l.id === editLayerID)) {
                return getters.layerByID(editLayerID);
            }
        }
        return null;
    },
    currentCueLayer: (state, getters) => {
        const userProjectData = getters.userProjectData;
        if (userProjectData && userProjectData.layers && userProjectData.layers.cueing) {
            const editLayerID = userProjectData.layers.cueing;
            if (getters.validCueLayers.some(l => l.id === editLayerID)) {
                return getters.layerByID(editLayerID);
            }
        }
        return null;
    },
    // Palette Tool
    selectedTool: (state) => {
        const tool = state.activeTool || localStorage.getItem('note-tool-selected') || 'pointerSelect';
        return tool.split('-')[0];
    },
    selectedDrawTool: (state) => {
        const tool = state.activeDrawTool || localStorage.getItem('note-draw-selected') || 'free_drawing';
        return tool.split('-')[0];
    },
    selectedScale: (state, getters) => {
        // state.pageScale wins while a project is open (mounted() seeds it from
        // storage). The storage read is the cold-start path and is now keyed per
        // project, falling back to the old flat key so existing zooms survive.
        const live = Number.parseFloat(state.pageScale);
        if (Number.isFinite(live) && live > 0) {
            return live;
        }
        return readPageScale(getters.project && getters.project.id);
    },
    selectedLoader: (state) => {
        const tool = state.pageLoader || Number.parseFloat(localStorage.getItem('page-loader')) || false;
        return tool;
    },
    selectedLoaderLogin: (state) => {
        const tool = state.pageLoaderLogin || Number.parseFloat(localStorage.getItem('page-loader-login')) || false;
        return tool;
    },
    // The five view settings below follow selectedScale above: the live state
    // field wins while a project is open (mounted() seeds it from storage), and
    // the storage read is the cold-start path, now keyed per project.
    selectedPageType: (state, getters) => {
        const pid = getters.project && getters.project.id;
        return state.pageType || readViewSetting('pageType', pid) || 'no';
    },
    selectedThumbnailView: (state, getters) => {
        const pid = getters.project && getters.project.id;
        return state.thumbnailView || readViewSetting('thumbnailView', pid) || 'false';
    },
    splitView: (state, getters) => {
        // state fields init to null so the storage fallback is reachable
        // ('true'/'false' are truthy, which would otherwise mask it).
        const pid = getters.project && getters.project.id;
        return state.splitView != null ? state.splitView : (readViewSetting('splitView', pid) || 'false');
    },
    splitPageSync: (state, getters) => {
        const pid = getters.project && getters.project.id;
        return state.splitPageSync != null ? state.splitPageSync : (readViewSetting('splitPageSync', pid) || 'true');
    },
    splitRatio: (state, getters) => {
        if (state.splitRatio != null) {
            return Number(state.splitRatio);
        }
        const pid = getters.project && getters.project.id;
        const stored = Number(readViewSetting('splitRatio', pid));
        return stored > 0 ? stored : 0.55;
    },
    selectedToolIndex: (state) => {
        const tool = state.activeTool || localStorage.getItem('note-tool-selected') || 'pointerSelect';
        const args = tool.split('-');
        if (args.length === 1) {
            return null;
        }
        return args[1];
    },
    // Collaborator Getters
    collaborators: (state, getters) => {
        const project = getters.project;
        if (!project || !project.id || !project.collaborators) return [];
        return Object.values(project.collaborators);
    },
    pendingCollaborators: (state, getters) => {
        const project = getters.project;
        if (!project.id) return [];
        return getters.invitations.filter(invitation => invitation.pid === project.id);
    },
    collaboratorByUID: (state, getters) => (uid) => {
        const filter = getters.collaborators.filter(collab => collab.uid === uid);
        if (filter.length > 0) {
            return filter[0];
        }
        return null;
    },

    // Note Getters
    notes: (state, getters) => {
        let notes = getters.project.notes || [];
        const version = getters.currentVersion;
        if (version) {
            notes = version.notes || [];
        }
        return Object.values(notes);
    },
    visibleNotes: (state, getters) => {
        const layerIDs = getters.visibleLayerIDs;
        const showNotes = getters.showNotesLayerIDs;
        try {
            return getters.notes.filter((note) => {
                const layerVisible = layerIDs.includes(note.layer);
                const layerShowingNotes = showNotes.includes(note.layer);
                return layerVisible && layerShowingNotes;
            });
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    notesByPage: (state, getters) => (page) => {
        try {
            return getters.visibleNotes.filter((note) => {
                return note.page === page;
            });
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    visiblePageLayerCues: (state, getters) => {
        const layerIDs = getters.visibleLayerIDs;
        const showCues = getters.showCuesLayerIDs;
        try {
            return getters.cues.filter((note) => {
                const layerVisible = layerIDs.includes(note.layer);
                const layerShowingCues = showCues && showCues.includes(note.layer);
                return layerVisible && layerShowingCues;
            });
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    cuesByPageLayer: (state, getters) => (page) => {
        try {
            return getters.visiblePageLayerCues.filter((note) => {
                return note.pageNumber === page;
            });
        } catch (e) {
            console.error(e);
        }
        return [];
    },
    // Bookmarks
    bookmarks: (state, getters) => {
        return getters.userProjectData.bookmarks || {};
    },
    // Per-user, per-project: hide Act/Scene/Song marks on the script and in print.
    //
    // `=== true` is load-bearing. Absent and false both mean VISIBLE, so existing
    // users need no backfill and no seeding. This is deliberately the opposite of
    // layer visibility (layers.viewing), which is an array that must be seeded at
    // membership time — copying that shape here would hide structure for everyone
    // who has never touched the setting.
    hideStructureMarks: (state, getters) => {
        return getters.userProjectData.hideStructureMarks === true;
    },
    // Floating Cues
    floatingCues: (state, getters) => {
        return getters.userProjectData.floatingCues || {};
    },
    // HeaderBox Options : Title, Description
    headerBoxOptions: (state, getters) => {
        return getters.userProjectData.headerBoxOptions || {};
    },
    userDefinedOptions: (state, getters) => {
        return getters.userProjectData.userDefinedOptions || {};
    },
    // titleFontSizeOptions
    titleFontSizeOptions: (state, getters) => {
        return getters.userProjectData.titleFontSizeOptions || {};
    },
    // cueLineOpacityOptions
    cueLineOpacityOptions: (state, getters) => {
        return getters.userProjectData.cueLineOpacityOptions || 0;
    },
    // cueBoxOpacityOptions
    cueBoxOpacityOptions: (state, getters) => {
        return getters.userProjectData.cueBoxOpacityOptions || 0;
    },
    // Cue Placement
    cuePlacement: (state, getters) => {
        return getters.userProjectData.cuePlacement || 'right';
    },
    showThumbnailPageUpdate: (state, getters) => {
        const user = getters.user;
        return (user && user.showThumbnailPageUpdate) || false;
    },
    // theme Getters
    showMode: (state, getters) => {
        // The print window is always light: paper is white (Josh, 11 Oct 2026).
        if (typeof window !== 'undefined' && isPrintPath(window.location.pathname)) return false;
        const user = getters.user;
        return (user && user.showMode) || false;
    },
    showMarginView: (state, getters) => {
        const user = getters.user;
        return (user && user.showMargin) || false;
    },
    newProjectLoading: (state) => {
        return state.newProjectLoading || false;
    },
    newDuplicateProjectLoading: (state) => {
        return state.newDuplicateProjectLoading || false;
    },
    projectBannerDisplay: (state, getters) => {
        return getters.project.projectBannerDisplay || false;
    },
    projectBannerUser: (state, getters) => {
        return getters.project.projectBannerUser || '';
    }
};


// WEBPACK FOOTER //
// ./src/lib/vuex/getters.js